import React, { useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin, Plus, Trash2, Loader2, Navigation } from "lucide-react";

export function PlacesManager({ destinations = [], addFn, removeFn, onChange, heading, subtitle }) {
  const [form, setForm] = useState({ label: "", address: "" });
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!form.label.trim() || !form.address.trim()) return;
    setBusy(true);
    try {
      await addFn(form);
      setForm({ label: "", address: "" });
      onChange?.();
      toast.success("Place saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id) => {
    try { await removeFn(id); onChange?.(); } catch (e) { toast.error("Couldn't remove"); }
  };

  return (
    <Card className="p-5 rounded-2xl" data-testid="places-manager">
      <div className="flex items-center gap-2">
        <Navigation className="h-4 w-4 text-primary" />
        <h3 className="font-heading font-semibold">{heading}</h3>
      </div>
      {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}

      {destinations.length > 0 && (
        <div className="mt-4 space-y-2">
          {destinations.map((d) => (
            <div key={d.id} className="flex items-center gap-2 p-2.5 rounded-xl bg-secondary" data-testid={`place-row-${d.id}`}>
              <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{d.label}</p>
                <p className="text-xs text-muted-foreground truncate">{d.address}</p>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive shrink-0" onClick={() => remove(d.id)} data-testid={`place-remove-${d.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <Input placeholder="Label (e.g. Work, Punahou)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="h-9 w-40" data-testid="place-label-input" />
        <Input placeholder="Address or place name" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-9 flex-1 min-w-[180px]" data-testid="place-address-input" />
        <Button size="sm" className="h-9 rounded-full gap-1" disabled={!form.label.trim() || !form.address.trim() || busy} onClick={add} data-testid="place-add-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
        </Button>
      </div>
    </Card>
  );
}
